<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useStore } from "vuex";
import { Icon } from "@iconify/vue";
import { useRouter, useRoute } from "vue-router";
import Swal from "sweetalert2";
import MenuItem from "@/components/ui/MenuItem.vue";

const store = useStore();
const router = useRouter();
const route = useRoute();

const currentUser = computed(() => store.state.userSlice.user);

const isHamburgerMenuOpen = computed(
  () => store.state.uiSlice.isHamburgerMenuOpen,
);

const getRoutesWithChildren = (routes) => {
  return routes
    .map((route) => {
      if (
        !route?.meta?.shouldAppearInMenu ||
        !route.path ||
        !route.meta?.menuName
      ) {
        return null;
      }

      const routeCopy = { ...route };

      if (route.children && route.children.length > 0) {
        routeCopy.children = route.children.filter(
          (child) => child.path && child.meta?.menuName,
        );
      } else {
        routeCopy.children = [];
      }

      return routeCopy;
    })
    .filter((route) => route !== null);
};

const allRoutes = computed(() => {
  return getRoutesWithChildren(router.options.routes);
});

const isAdminOpen = ref(false);

watch(
  () => route.fullPath,
  () => {
    isAdminOpen.value = route.path.startsWith("/admin");
  },
  { immediate: true },
);

const updateHamburgerMenuState = () => {
  const isDesktop = window.innerWidth >= 1024;
  setTimeout(() => {
    store.commit("uiSlice/setHamburgerMenuOpen", isDesktop);
  }, 50);
};

// --- logout ---
const handleLogout = () => {
  Swal.fire({
    title: "آیا مطمئن هستید؟",
    text: "برای خروج از سیستم تایید کنید.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "بله، خروج",
    cancelButtonText: "لغو",
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
  }).then((result) => {
    if (result.isConfirmed) {
      store.commit("userSlice/userLogout");
      router.push("/login");
      Swal.fire({
        title: "خروج با موفقیت انجام شد!",
        icon: "success",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  });
};

onMounted(() => {
  updateHamburgerMenuState();
  window.addEventListener("resize", updateHamburgerMenuState);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateHamburgerMenuState);
});
</script>

<template>
  <div
    ref="hamburgerMenu"
    :class="{
      'w-[16rem] mobile:-translate-x-0 tablet:-translate-x-0 translate-zero laptop:fixed':
        isHamburgerMenuOpen,
      'w-[5rem] mobile:translate-x-full tablet:translate-x-full':
        !isHamburgerMenuOpen,
    }"
    class="min-h-full bg-white text-black transition-all duration-300 ease-in-out border-l relative tablet:fixed z-[100] mobile:z-[100] mobile:fixed dark:bg-neutral-800 dark:border-neutral-700"
  >
    <div class="px-5 mt-20">
      <img
        src="@/assets/images/logos/directam-Logo1.png"
        alt="logo"
        class="object-contain tablet:hidden mobile:hidden"
      />
    </div>

    <div class="space-y-4 p-4 text-icons sticky top-16">
      <!-- ADMIN ROUTE -->
      <MenuItem
        v-if="currentUser?.role === 1 || currentUser?.id == 8"
        :route="allRoutes[0]"
      />

      <!--      REST OF THE ROUTES-->
      <MenuItem
        v-for="route in allRoutes.filter((r) => r.path !== '/admin')"
        :key="route.path"
        :route="route"
      />

      <!--      LOGOUT ROUTE-->
      <div
        @click="handleLogout"
        class="flex gap-2 items-center justify-start transition-colors p-2 cursor-pointer hover:bg-red-500 hover:text-white rounded-lg dark:text-neutral-400"
      >
        <Icon icon="material-symbols:logout" class="text-3xl" />
        <span
          :class="{ hidden: !isHamburgerMenuOpen, block: isHamburgerMenuOpen }"
          class="select-none"
          >خروج</span
        >
      </div>
    </div>
  </div>
</template>
